import { tokens, Typography } from "@sunrun/experience-ui-components";
import styled from "@emotion/styled/macro";
import {
  ReviewCashSummary,
  ReviewFinancialSummary,
  ReviewFlexMonthlySummary,
  ReviewLoanSummary,
  ReviewMonthlySummary,
  ReviewPrepaidSummary,
} from "../../../amplify/backend/function/offerexpstoreFrontApi/ts/public/offerTypes";
import {
  RowContainer,
  RowContainerWithTooltip,
} from "components/atoms/PriceGlobals";
import { StyledCategory, StyledHeader } from "components/atoms/GlobalStyles";
import {
  COSTCO_CARD_ESTIMATE,
  FEDERAL_SOLAR_TAX_CREDIT,
  SRECII,
  STATE_LOCAL_REBATES,
} from "constants/pricing-tooltips";

type OfferReviewRebatesAndIncentivesProps = {
  summary: ReviewFinancialSummary;
  singleColumn?: boolean;
  nonSolarView?: boolean;
};

const CategoryNoMargin = styled(StyledCategory)`
  margin: 0;
`;

const OfferReviewRebatesAndIncentives = ({
  summary,
  singleColumn = false,
  nonSolarView = false,
}: OfferReviewRebatesAndIncentivesProps) => {
  switch (summary.type) {
    case "Cash": {
      const s = summary as ReviewCashSummary;
      if (s.rebates <= 0 && s.srec <= 0 && s.taxCredit <= 0) {
        return null;
      }
      return (
        <CategoryNoMargin>
          {!singleColumn && (
            <StyledHeader>
              <Typography variant="h3" color={tokens.BRAND_HERO_BLUE}>
                Rebates & Incentives
              </Typography>
            </StyledHeader>
          )}
          {s.taxCredit > 0 && !nonSolarView && (
            <RowContainerWithTooltip
              tooltip={FEDERAL_SOLAR_TAX_CREDIT}
              label="Estimated Federal Solar Tax Credit"
              cost={s.taxCredit}
              accentColor={tokens.BRAND_HERO_BLUE}
            />
          )}
          {s.rebates > 0 && (
            <RowContainerWithTooltip
              tooltip={STATE_LOCAL_REBATES}
              label="Estimated State and Local Rebates"
              cost={s.rebates}
              accentColor={tokens.BRAND_HERO_BLUE}
            />
          )}
          {s.srec > 0 && (
            <RowContainerWithTooltip
              tooltip={SRECII}
              label="SRECII (SREC2)"
              cost={s.srec}
              accentColor={tokens.BRAND_HERO_BLUE}
            />
          )}
        </CategoryNoMargin>
      );
    }
    case "Loan": {
      const s = summary as ReviewLoanSummary;
      if (
        s.taxCredit <= 0 &&
        s.rebates <= 0 &&
        s.srec <= 0 &&
        s.costcoCard <= 0
      ) {
        return null;
      }
      return (
        <CategoryNoMargin>
          {!singleColumn && (
            <StyledHeader>
              <Typography variant="h3" color={tokens.BRAND_HERO_BLUE}>
                Rebates & Incentives
              </Typography>
            </StyledHeader>
          )}
          {s.taxCredit > 0 && (
            <RowContainerWithTooltip
              tooltip={FEDERAL_SOLAR_TAX_CREDIT}
              label="Estimated Federal Solar Tax Credit"
              cost={s.taxCredit}
              accentColor={tokens.BRAND_HERO_BLUE}
            />
          )}
          {s.rebates > 0 && (
            <RowContainerWithTooltip
              tooltip={STATE_LOCAL_REBATES}
              label="Estimated State and Local Rebates"
              cost={s.rebates}
              accentColor={tokens.BRAND_HERO_BLUE}
            />
          )}
          {s.srec > 0 && (
            <RowContainerWithTooltip
              tooltip={SRECII}
              label="SRECII (SREC2)"
              cost={s.srec}
              accentColor={tokens.BRAND_HERO_BLUE}
            />
          )}
          {s.costcoCard > 0 && (
            <RowContainerWithTooltip
              tooltip={COSTCO_CARD_ESTIMATE}
              label="Est. Costco Shop Card"
              cost={s.costcoCard}
              accentColor={tokens.BRAND_HERO_BLUE}
            />
          )}
        </CategoryNoMargin>
      );
    }
    case "Monthly": {
      const s = summary as ReviewMonthlySummary;
      if (s.rebates <= 0 && s.srec <= 0 && s.costcoCard <= 0) {
        return null;
      }
      return (
        <CategoryNoMargin>
          {!singleColumn && (
            <StyledHeader>
              <Typography variant="h3" color={tokens.BRAND_HERO_BLUE}>
                Rebates & Incentives
              </Typography>
            </StyledHeader>
          )}
          {s.rebates > 0 && (
            <RowContainer
              label="Estimated State and Local Rebates"
              cost={s.rebates}
              accentColor={tokens.BRAND_HERO_BLUE}
            />
          )}
          {s.srec > 0 && (
            <RowContainerWithTooltip
              tooltip={SRECII}
              label="SRECII (SREC2)"
              cost={s.srec}
              accentColor={tokens.BRAND_HERO_BLUE}
            />
          )}
          {s.costcoCard > 0 && (
            <RowContainerWithTooltip
              tooltip={COSTCO_CARD_ESTIMATE}
              label="Est. Costco Shop Card"
              cost={s.costcoCard}
              accentColor={tokens.BRAND_HERO_BLUE}
            />
          )}
        </CategoryNoMargin>
      );
    }
    case "FlexMonthly": {
      const s = summary as ReviewFlexMonthlySummary;
      if (s.rebates <= 0 && s.srec <= 0 && s.costcoCard <= 0) {
        return null;
      }
      return (
        <CategoryNoMargin>
          {!singleColumn && (
            <StyledHeader>
              <Typography variant="h3" color={tokens.BRAND_HERO_BLUE}>
                Rebates & Incentives
              </Typography>
            </StyledHeader>
          )}
          {s.rebates > 0 && (
            <RowContainer
              label="Estimated State and Local Rebates"
              cost={s.rebates}
              accentColor={tokens.BRAND_HERO_BLUE}
            />
          )}
          {s.srec > 0 && (
            <RowContainerWithTooltip
              tooltip={SRECII}
              label="SRECII (SREC2)"
              cost={s.srec}
              decimals={2}
              accentColor={tokens.BRAND_HERO_BLUE}
            />
          )}
          {s.costcoCard > 0 && (
            <RowContainerWithTooltip
              tooltip={COSTCO_CARD_ESTIMATE}
              label="Est. Costco Shop Card"
              cost={s.costcoCard}
              decimals={2}
              accentColor={tokens.BRAND_HERO_BLUE}
            />
          )}
        </CategoryNoMargin>
      );
    }
    case "Prepaid": {
      const s = summary as ReviewPrepaidSummary;
      if (s.rebates <= 0 && s.srec <= 0) {
        return null;
      }
      return (
        <CategoryNoMargin>
          {!singleColumn && (
            <StyledHeader>
              <Typography variant="h3" color={tokens.BRAND_HERO_BLUE}>
                Rebates & Incentives
              </Typography>
            </StyledHeader>
          )}
          {s.rebates > 0 && (
            <RowContainerWithTooltip
              tooltip={STATE_LOCAL_REBATES}
              label="Estimated State and Local Rebates"
              cost={s.rebates}
              accentColor={tokens.BRAND_HERO_BLUE}
            />
          )}
          {s.srec > 0 && (
            <RowContainerWithTooltip
              tooltip={SRECII}
              label="SRECII (SREC2)"
              cost={s.srec}
              accentColor={tokens.BRAND_HERO_BLUE}
            />
          )}
        </CategoryNoMargin>
      );
    }
    default:
      return null;
  }
};

export { OfferReviewRebatesAndIncentives };
